"use client";

import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/i18n/routing";
import { EJTECH_ARTICLE_ROUTE } from "@/lib/routes";
import { EJTECH_COLORS, EJTECH_TYPE } from "@/lib/theme/ej-tech";
import { FINANCING_ROWS } from "@/fixtures/ej-tech/financing-fixtures";

/**
 * The 融資紀錄 table and its year filter.
 *
 * The chips were static: `active` was hardcoded to the first one and no chip
 * carried a handler, so pressing 2024 did nothing and the table always showed
 * everything. QA: "ensure the tab items to be functional", and "add 2026 to the
 * tab items".
 *
 * The years are DERIVED from the rows rather than hand-listed, so a tab can
 * never exist without data behind it — which is exactly how a 2026 tab would
 * otherwise have opened an empty table.
 */
const ALL = "全部";

/** "26年02月" -> "2026". */
function yearOf(date: string): string | null {
  const m = /^(\d{2})年/.exec(date);
  return m ? `20${m[1]}` : null;
}

export function FinancingTable() {
  const years = useMemo(() => {
    const found = [...new Set(FINANCING_ROWS.map((r) => yearOf(r.date)))]
      .filter((y): y is string => Boolean(y))
      .sort((a, b) => Number(b) - Number(a));
    return [ALL, ...found];
  }, []);

  const [active, setActive] = useState<string>(ALL);

  const rows = useMemo(
    () =>
      active === ALL
        ? FINANCING_ROWS
        : FINANCING_ROWS.filter((r) => yearOf(r.date) === active),
    [active]
  );

  /*
    Every row, in one table.

    Pagination was added when the fixture held 41 rows and the page had no other
    way to end; with a year filter above it, twenty-at-a-time meant a reader
    comparing a year's rounds had to page through them. QA: "remove the
    pagination, show all result in one page table".
  */
  const visible = rows;

  return (
    <div className="flex flex-col gap-8">
      {/*
        One row that SCROLLS, rather than a block that wraps. With eight years
        the tabs wrapped onto two and three lines on a phone and pushed the
        table off the screen; a filter row is a strip you move through. QA: "for
        the tab menu, make it horizontally scrollable if the tab items are more
        than one row".
      */}
      <div
        role="tablist"
        aria-label="按年份篩選"
        className="-mx-4 flex items-center gap-2 overflow-x-auto px-4 py-1 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {years.map((y) => {
          const isActive = y === active;
          return (
            <button
              key={y}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(y)}
              // Resting colours on custom properties: an inline declaration
              // outranks every `hover:` utility, and the `border` SHORTHAND has
              // to be a utility or `hover:border-*` loses to it.
              className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-[color:var(--rest-border)] bg-[color:var(--rest-bg)] px-4 py-2 text-[color:var(--rest-fg)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[color:var(--hover-border)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              style={
                {
                  ...EJTECH_TYPE.navLink,
                  fontSize: 13,
                  fontWeight: 600,
                  ["--rest-fg" as string]: isActive
                    ? EJTECH_COLORS.textOnDark
                    : EJTECH_COLORS.ink,
                  ["--rest-bg" as string]: isActive
                    ? EJTECH_COLORS.primary
                    : EJTECH_COLORS.surface,
                  ["--rest-border" as string]: isActive
                    ? EJTECH_COLORS.primary
                    : EJTECH_COLORS.hairline,
                  ["--hover-border" as string]: EJTECH_COLORS.primary,
                } as React.CSSProperties
              }
            >
              {y}
            </button>
          );
        })}
      </div>

      {/*
        The table FITS from 390px up rather than scrolling sideways: the cells
        lose their generous padding on a phone, the company name is allowed to
        wrap instead of forcing the row wide, and 閱讀 keeps only its arrow
        until there is room for the word. QA: "try to fix the table into screen
        (without needing to scroll) if the device width is greater than 390px".
        `overflow-x-auto` stays as the backstop below that.
      */}
      <div
        className="w-full overflow-x-auto"
        style={{ border: `1px solid ${EJTECH_COLORS.hairline}` }}
      >
        <table className="w-full table-fixed border-collapse text-left">
          {/* The last column carries the word 閱讀 at every width, so it is
              wide enough for it — QA: "for mobile size, do not hide 閱讀 in the
              arrow button". The company name keeps the remaining width and
              wraps, which is the column that can afford to. */}
          <colgroup>
            <col className="w-[62px] sm:w-[96px]" />
            <col />
            <col className="w-[96px] sm:w-[140px]" />
            <col className="w-[64px] sm:w-[92px]" />
          </colgroup>
          <thead>
            <tr
              style={{
                backgroundColor: EJTECH_COLORS.surfaceAlt,
                borderBottom: `1px solid ${EJTECH_COLORS.hairline}`,
              }}
            >
              {["日期", "公司名稱", "融資金額（USD）", "相關"].map((h, i) => (
                <th
                  key={h}
                  scope="col"
                  // The header wraps on a phone rather than running out of its
                  // cell: 融資金額（USD） is wider than the 104px column it
                  // sits in, so `nowrap` pushed it under the cell beside it.
                  // QA: "for mobile size, may wrap the header row text into two
                  // lines if the text is too long". It stays on one line from
                  // `sm`, where there is room.
                  className={`px-2 py-4 align-bottom sm:px-5 sm:whitespace-nowrap ${i === 3 ? "text-right" : ""}`}
                  style={{
                    ...EJTECH_TYPE.eyebrow,
                    fontSize: 12,
                    color: EJTECH_COLORS.ink,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr
                key={`${row.date}-${row.name}-${i}`}
                className="transition-colors hover:bg-black/[0.015]"
                style={{
                  borderBottom:
                    i < visible.length - 1
                      ? `1px solid ${EJTECH_COLORS.hairlineSoft}`
                      : undefined,
                }}
              >
                <td
                  className="px-2 py-4 align-middle sm:px-5"
                  style={{
                    ...EJTECH_TYPE.meta,
                    fontSize: 13,
                    color: EJTECH_COLORS.muted,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.date}
                </td>
                <td
                  className="px-2 py-4 align-middle sm:px-5"
                  style={{
                    ...EJTECH_TYPE.cardTitle,
                    fontSize: 15,
                    color: EJTECH_COLORS.ink,
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.name}
                </td>
                <td className="px-2 py-4 align-middle sm:px-5">
                  <span
                    className="inline-flex w-fit items-center rounded-full px-2 py-1 sm:px-3"
                    style={{
                      ...EJTECH_TYPE.pill,
                      fontSize: 12,
                      color: EJTECH_COLORS.accent,
                      backgroundColor: EJTECH_COLORS.accentSoft,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.amount}
                  </span>
                </td>
                <td className="px-2 py-4 align-middle text-right sm:px-5">
                  {/* Was a hardcoded `/zh-TW/ej-tech/article`, which pinned a
                      Simplified reader into the Traditional tree. */}
                  <Link
                    href={EJTECH_ARTICLE_ROUTE}
                    className="inline-flex items-center gap-0.5 transition-colors hover:opacity-80 sm:gap-1"
                    style={{
                      ...EJTECH_TYPE.navLink,
                      fontSize: 12,
                      color: EJTECH_COLORS.primary,
                      whiteSpace: "nowrap",
                    }}
                  >
                    閱讀
                    <ArrowUpRight className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" strokeWidth={2.4} />
                  </Link>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-5 py-10 text-center"
                  style={{ ...EJTECH_TYPE.body, color: EJTECH_COLORS.muted }}
                >
                  {active} 年沒有融資紀錄。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
